import { useState, useEffect, useCallback } from 'react';
import type { PinLockState } from '../../hooks/usePinLock';
import { useSystemSettings } from '../../hooks/useSystemSettings';

interface PinLockScreenProps {
  pinLock: PinLockState;
  userName: string;
  avatarUrl: string | null;
  initials: string;
  onSignOut: () => Promise<void>;
}

/**
 * PinLockScreen — full-page lock screen rendered when PIN is active.
 * Content (dashboard, pages) is NOT rendered below this component.
 * This replaces the old overlay approach to prevent any flash of protected content.
 */
export const PinLockOverlay = ({
  pinLock,
  userName,
  avatarUrl,
  initials,
  onSignOut,
}: PinLockScreenProps) => {
  const [pin, setPin] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const { settings } = useSystemSettings();

  const MAX_ATTEMPTS = 5;
  const appName = settings.platform_name || 'PandaPRO';
  const logoUrl  = settings.logo_url || null;

  const handleDigit = useCallback((digit: string) => {
    if (isChecking) return;
    setPin(p => (p.length < 4 ? p + digit : p));
  }, [isChecking]);

  const handleDelete = useCallback(() => {
    setPin(p => p.slice(0, -1));
  }, []);

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length === 4) {
      const verify = async () => {
        setIsChecking(true);
        const ok = await pinLock.verifyPin(pin);
        setIsChecking(false);
        if (!ok) {
          setShakeKey(k => k + 1);
          setPin('');
        }
      };
      verify();
    }
  }, [pin, pinLock]);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') handleDigit(e.key);
      else if (e.key === 'Backspace') handleDelete();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDigit, handleDelete]);

  const handleSignOut = async () => {
    setSigningOut(true);
    await onSignOut();
  };

  const keys = ['1','2','3','4','5','6','7','8','9','','0','⌫'];

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center bg-[#F8FAFB]"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {/* App branding — top */}
      <div className="absolute top-8 left-0 right-0 flex justify-center">
        <div className="flex items-center gap-2.5">
          {logoUrl ? (
            <img src={logoUrl} alt={appName} className="h-8 w-auto object-contain" />
          ) : (
            <div className="w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center">
              <i className="ri-leaf-line text-white text-base" />
            </div>
          )}
          <span className="text-base font-700 text-gray-800">{appName}</span>
        </div>
      </div>

      {/* Lock card */}
      <div className="flex flex-col items-center gap-6 w-full max-w-xs px-4">

        {/* User avatar */}
        <div className="flex flex-col items-center gap-3">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={userName}
              className="w-20 h-20 rounded-full object-cover object-top border-4 border-white"
              style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.10)' }}
            />
          ) : (
            <div
              className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center text-white text-2xl font-700 border-4 border-white"
              style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.10)' }}
            >
              {initials}
            </div>
          )}
          <div className="text-center">
            <p className="text-base font-700 text-gray-900">{userName}</p>
            <p className="text-sm text-gray-400 mt-0.5">Digite seu PIN para continuar</p>
          </div>
        </div>

        {/* PIN dots + error */}
        <div key={shakeKey} className={shakeKey > 0 ? 'animate-[shake_0.35s_ease-in-out]' : ''}>
          <div className="flex gap-4 justify-center mb-1">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full border-2 transition-all duration-150 ${
                  i < pin.length
                    ? 'bg-emerald-500 border-emerald-500 scale-110'
                    : 'bg-transparent border-gray-300'
                }`}
              />
            ))}
          </div>
          {pinLock.pinError && (
            <p className="text-xs text-red-500 text-center mt-2 font-500">{pinLock.pinError}</p>
          )}
        </div>

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-3 w-56">
          {keys.map((key, idx) => {
            if (key === '') return <div key={idx} />;
            if (key === '⌫') {
              return (
                <button
                  key={idx}
                  onClick={handleDelete}
                  disabled={isChecking}
                  className="h-14 rounded-2xl bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-500 transition-all active:scale-95 cursor-pointer disabled:opacity-40"
                >
                  <i className="ri-delete-back-2-line text-lg" />
                </button>
              );
            }
            return (
              <button
                key={idx}
                onClick={() => handleDigit(key)}
                disabled={isChecking || pin.length >= 4}
                className="h-14 rounded-2xl bg-white border border-gray-200 hover:bg-emerald-50 hover:border-emerald-200 flex items-center justify-center text-xl font-600 text-gray-800 transition-all active:scale-95 cursor-pointer disabled:opacity-40"
              >
                {isChecking && pin.length === 4 ? (
                  <i className="ri-loader-4-line animate-spin text-base text-emerald-500" />
                ) : key}
              </button>
            );
          })}
        </div>

        {/* Sign out / forgot PIN */}
        <div className="flex flex-col items-center gap-2 mt-2 w-full">
          {pinLock.attempts >= MAX_ATTEMPTS ? (
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
            >
              <i className="ri-logout-box-r-line" />
              {signingOut ? 'Saindo...' : 'Sair e fazer login novamente'}
            </button>
          ) : (
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            >
              <i className="ri-logout-box-r-line text-xs" />
              {signingOut ? 'Saindo...' : 'Esqueceu o PIN? Sair da conta'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-8px); }
          40%       { transform: translateX(8px); }
          60%       { transform: translateX(-6px); }
          80%       { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
};
